import { useEffect, useRef, useState, type CSSProperties } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { formatTime } from '../../utils/time';

import classes from './Timer.module.scss';
import { dateDiffInSeconds } from './utils';

export type TimerProps = {
  endDate: Date;
  prefix?: string;
  className?: string;
  style?: CSSProperties;
  onClick?: () => void;
  onTimerEnd?: () => void;
  /**
   * Fire `onTimerEnd` only on the way down to zero, never for an end that had already passed when the
   * timer mounted or was handed a new `endDate`. Off by default: the widgets that refetch on the end
   * count on that call a second after mounting expired. For a caller whose refetch can bring back the
   * same passed end, where firing for it again would refetch in a loop.
   */
  skipEndIfExpiredOnMount?: boolean;
  dataTestId?: string;
};

/** Whole seconds left, never below zero: an end that has passed reads 00:00:00, not a negative time. */
const secondsLeft = (endDate: Date) => Math.max(0, dateDiffInSeconds(endDate));

export const Timer = ({
  className,
  endDate,
  prefix,
  onClick,
  onTimerEnd,
  skipEndIfExpiredOnMount = false,
  ...otherProps
}: TimerProps) => {
  const [seconds, setSeconds] = useState(() => secondsLeft(endDate || new Date()));
  const { t } = useTranslation();

  const onTimerEndRef = useRef(onTimerEnd);
  useEffect(() => {
    onTimerEndRef.current = onTimerEnd;
  }, [onTimerEnd]);

  useEffect(() => {
    if (!endDate) return;

    const initial = secondsLeft(endDate);
    setSeconds(initial);

    // An end that has already passed has nothing to count; by default it still fires on the first tick.
    // Compared in milliseconds: the whole seconds shown floor an end under a second away to zero, and
    // that end has not passed yet.
    if (skipEndIfExpiredOnMount && endDate.getTime() <= Date.now()) return;

    const interval = setInterval(() => {
      const next = secondsLeft(endDate);
      setSeconds(next);
      if (next <= 0) {
        clearInterval(interval);
        onTimerEndRef.current?.();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [endDate, skipEndIfExpiredOnMount]);

  const formatted = formatTime(seconds, 's', t);

  return (
    <div
      className={classNames(classes.timer, className)}
      {...otherProps}
      data-testid={otherProps.dataTestId}
      style={{
        ...otherProps?.style,
      }}
      onClick={onClick}
    >
      {prefix} {formatted}
    </div>
  );
};
